import { useState } from 'react';
import warningIMG from '@/assets/imgs/icons/warning.svg';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components';
import {
  Button,
  ButtonBack,
  Checkbox,
  CurrencyFormat,
  Error,
  Field,
  Icon,
  Image,
  LoadingStatus,
  Option,
  SuccessStatus,
} from '@/components/generic';
import { temporaryTiers } from '@/constants';
import {
  formatEthersToAmount,
  formatSensitiveCardNumber,
  formatUnitsToAmount,
} from '@/utils';
import {
  TUserCardInfo,
  useGetDepositQuote,
} from '@xhype/card-app-sdk';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useGetTokenBalances,
  useHandleCardDeposit,
} from '@package/smart-wallet';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

/* Schemas */
export const CardDepositQuoteFormSchema = z.object({
  amount: z
    .string()
    .min(1, { message: 'Amount is required' })
    .refine((value) => !isNaN(Number(value)), {
      message: 'Amount must be a valid number',
    }),
  // .refine((value) => Number(value) >= 20, {
  //   message: 'Amount must be greater than 20',
  // }),
  couponCode: z.string().optional(),
});

/* Types */
export type CardDepositQuoteFormType = z.infer<
  typeof CardDepositQuoteFormSchema
>;
export const DepositCard = ({
  onClose,
  cardInfo,
}: {
  onClose: () => void;
  cardInfo: Omit<TUserCardInfo, 'cvv' | 'validPeriod'> | null;
}) => {
  const [isDataForm, setIsDataForm] = useState({
    terms: false,
  });
  const [amount, setAmount] = useState<string>('0');
  const [couponCode, setCouponCode] = useState<string | undefined>(undefined);
  const cardId = cardInfo?.id || 0;
  const { tokenAmount, tokenDecimals } = useGetTokenBalances();

  /* Search Form */
  const searchForm = useForm<CardDepositQuoteFormType>({
    resolver: zodResolver(CardDepositQuoteFormSchema),
  });
  /* Handlers */
  const handleSearch = async (data: CardDepositQuoteFormType) => {
    setAmount(data.amount);
    setCouponCode(data.couponCode);
  };
  if (Number(amount) > 0) {
    return (
      <GetDepositQuote
        amount={amount}
        cardId={cardId}
        couponCode={couponCode}
        onClose={() => setAmount('0')}
        cardInfo={cardInfo}
      />
    );
  }
  return (
    <>
      <div className='mb-8 text-h4 max-md:text-h5 text-t-primary'>
        Deposit on Card
      </div>
      <div className='flex items-center mb-2'>
        <div className='shrink-0'>
          <Image
            className='size-20 rounded-2xl'
            src={
              temporaryTiers[
                (cardInfo?.tier?.id as keyof typeof temporaryTiers) || 'Tier 0'
              ]?.img
            }
            alt=''
          />
        </div>
        <div className='w-[calc(100%-5rem)] pl-6'>
          <div className='truncate text-h6 text-t-primary'>
            {cardInfo?.alias
              ? `${cardInfo.alias} - ${cardInfo?.cardNumber}`
              : formatSensitiveCardNumber(cardInfo?.cardNumber)}
          </div>
          <div className='mt-1 flex gap-1 text-body-1 text-t-secondary/80'>
            Card Balance{' '}
            <CurrencyFormat
              value={Number(cardInfo?.balanceInfo?.amount || '0')}
            />
          </div>
          <div className='mt-1 flex gap-1 text-body-1 text-t-secondary/80'>
            Wallet Balance{' '}
            <CurrencyFormat
              decimalAmount={4}
              value={Number(
                formatUnitsToAmount({
                  string: tokenAmount || '0',
                  decimalToken: tokenDecimals,
                }),
              )}
            />
          </div>
        </div>
      </div>
      <div className='mb-8 text-body-2 font-medium text-t-tertiary'>
        You're depositing. Click{' '}
        <span className='text-primary-02'>Get Quote</span> and please ensure you
        have sufficient balance in your account to cover the card issuance fee.
      </div>
      <Form {...searchForm}>
        <form
          onSubmit={searchForm.handleSubmit(handleSearch)}
          className='w-full flex flex-col items-center justify-center gap-4'
        >
          <section className='w-full flex flex-row items-center justify-center gap-4'>
            <FormField
              control={searchForm.control}
              name='amount'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormControl>
                    <Field
                      classInput='pl-12.5'
                      label='Price (USD)'
                      placeholder='Enter price'
                      tooltip='The price of the card in USD'
                      required
                      step='any'
                      min={0}
                      type='number'
                      {...field}
                      autoComplete='off'
                      inputMode='decimal'
                    >
                      <div className='absolute top-1/2 -translate-y-1/2 left-1 w-10 h-10 flex items-center justify-center bg-secondary-04 rounded-full pointer-events-none'>
                        <Icon className='fill-black' name='usd-circle' />
                      </div>
                    </Field>
                  </FormControl>
                  <div className='text-primary-03/80 '>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          </section>

          <section className='w-full flex flex-row items-center justify-center gap-4'>
            <FormField
              control={searchForm.control}
              name='couponCode'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormControl>
                    <Field
                      classInput='pl-12.5'
                      label='Coupon Code'
                      placeholder='Enter coupon code'
                      tooltip='The coupon code for the card'
                      {...field}
                    >
                      <div className='absolute top-1/2 -translate-y-1/2 left-1 w-10 h-10 flex items-center justify-center bg-secondary-04 rounded-full pointer-events-none'>
                        <Icon className='fill-black' name='coupon' />
                      </div>
                    </Field>
                  </FormControl>
                  <div className='text-primary-03/80 '>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          </section>
          <Checkbox
            label='I understand that I am depositing and I need to have sufficient balance in my account to cover the card issuance fee.'
            checked={isDataForm.terms}
            onChange={(checked) =>
              setIsDataForm({ ...isDataForm, terms: checked })
            }
            required
            name='terms'
            id='terms'
            value='terms'
          />
          <div className='flex justify-end gap-3 mt-8 w-full'>
            <Button className='flex-1' isStroke type='button' onClick={onClose}>
              Cancel
            </Button>
            <Button
              className='flex-1'
              isBlack={isDataForm?.terms}
              isWhite={!isDataForm?.terms}
              type='submit'
              disabled={!isDataForm?.terms}
            >
              Get deposit quote
            </Button>
          </div>
        </form>
      </Form>
    </>
  );
};
type GetDepositQuoteProps = {
  amount: string;
  cardId: number;
  couponCode?: string;
  onClose?: () => void;
  cardInfo: any;
};
/* Schemas */
export const CardDepositFormSchema = z.object({
  amount: z
    .string()
    .min(1, { message: 'Amount is required' })
    .refine((value) => !isNaN(Number(value)), {
      message: 'Amount must be a valid number',
    })
    .refine((value) => Number(value) >= 20, {
      message: 'Amount must be greater than 20',
    }),
  holderId: z.string().refine((value) => !value || !isNaN(Number(value)), {
    message: 'Holder Id must be a valid number',
  }),
  cardId: z.string().refine((value) => !value || !isNaN(Number(value)), {
    message: 'Card Id must be a valid number',
  }),
});
/* Types */
export type CardDepositFormType = z.infer<typeof CardDepositFormSchema>;
const GetDepositQuote = ({
  amount,
  cardId,
  couponCode,
  onClose,
  cardInfo,
}: GetDepositQuoteProps) => {
  const [isSuccessTx, setIsSuccessTx] = useState<string | false>(false);

  const {
    isLoading,
    data: depositQuoteData,
    error: errorDepositQuote,
  } = useGetDepositQuote({
    amount,
    cardId,
    couponCode,
  });
  const {
    handleDeposit: handleDepositCallback,
    isLoading: isLoadingDeposit,
    error: errorDeposit,
  } = useHandleCardDeposit();
  /* Handlers */
  const handleDeposit = async () => {
    try {
      const txHash = await handleDepositCallback(
        amount,
        couponCode,
        Number(cardInfo?.holderId),
        Number(cardId),
      );
      console.log('Deposit successful. Transaction hash:', txHash);
      setIsSuccessTx(txHash);
    } catch (error) {
      console.error('Error depositing: ', error);
    }
  };
  if (isSuccessTx) {
    return (
      <SuccessStatus
        content='Your transaction was sent successfully!'
        onClose={() => window.location.reload()}
        txHash={isSuccessTx}
      />
    );
  }
  if (isLoading || isLoadingDeposit) {
    return <LoadingStatus />;
  }
  /* Render */
  return (
    <>
      <ButtonBack
        title='New Quote'
        className='mb-0'
        onClick={() => onClose?.()}
      />
      <div className='flex justify-center items-center size-16  bg-primary-03/15 rounded-full'>
        <Image className='size-6 ' src={warningIMG} alt='' />
      </div>
      <div className='mb-2 text-h4 max-md:text-h5 text-t-primary'>
        Quote for Deposit
      </div>
      <div className='mb-4 text-body-2 font-medium text-t-tertiary'>
        Please review the details below . This action can't be undone, and you
        will need to deposit the specified amount to proceed with the card
        creation. time.
      </div>
      <div className='flex flex-col w-full mb-4'>
        {/* <Option classTitle='w-38' title='Platform Fee'>
          <CurrencyFormat
            currency=''
            value={Number(
              formatEthersToAmount({
                string: depositQuoteData?.platformFee || 0,
              }),
            )}
          />{' '}
          USDT
        </Option>
        <Option classTitle='w-38 [&:+]:border' title='Business Fee'>
          <CurrencyFormat
            currency=''
            value={Number(
              formatEthersToAmount({
                string: depositQuoteData?.businessFee || 0,
              }),
            )}
          />{' '}
          USDT
        </Option>
        <Option classTitle='w-38 [&:+]:border' title='Provider Fee'>
          <CurrencyFormat
            currency=''
            value={Number(
              formatEthersToAmount({
                string: depositQuoteData?.providerFee || 0,
              }),
            )}
          />{' '}
          USDT
        </Option>
        <Option classTitle='w-38 [&:+]:border' title='Total Fee'>
          <CurrencyFormat
            currency=''
            value={Number(
              formatEthersToAmount({
                string: depositQuoteData?.totalFee || 0,
              }),
            )}
          />{' '}
          USDT
        </Option> */}
        <Option
          classTitle='w-38 [&:+]:border'
          title='Amount to Send'
          color='bg-primary-04'
        >
          <CurrencyFormat
            currency=''
            value={Number(
              formatEthersToAmount({
                string: depositQuoteData?.amountToSend || 0,
              }),
            )}
          />{' '}
          USDT
        </Option>
        <Option
          classTitle='w-38 [&:+]:border'
          title='Amount to Receive'
          color='bg-primary-03'
        >
          <CurrencyFormat
            currency=''
            value={Number(
              formatEthersToAmount({
                string: depositQuoteData?.amountToReceive || 0,
              }),
            )}
          />{' '}
          USDT
        </Option>
      </div>
      <Error
        error={errorDepositQuote}
        message={
          // Check if errorDepositQuote is an AxiosError with response.data.msg
          (errorDepositQuote &&
            typeof errorDepositQuote === 'object' &&
            'response' in errorDepositQuote &&
            (errorDepositQuote as any).response?.data?.msg) ||
          errorDepositQuote?.message ||
          'Error While fetching card creation quote'
        }
      />
      <Error
        error={errorDeposit}
        message={errorDeposit || 'Error While fetching depositing to card'}
      />
      <div className='flex justify-end gap-3 w-full'>
        <Button className='flex-1' isStroke type='button' onClick={onClose}>
          Cancel
        </Button>
        {!errorDepositQuote && (
          <Button
            className='flex-1'
            isBlack
            type='button'
            onClick={() => handleDeposit()}
          >
            Deposit Amount
          </Button>
        )}
      </div>
    </>
  );
};
